import { ResponsiveTable } from "../responsiveTable/ResponsiveTable";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ActionIcon, Alert, Badge, Button, Group, NumberInput, SegmentedControl, Select, Stack, Table, Text,
  Textarea,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle, IconQrcode, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import type {
  IssueRecipient, IssueRecipientType, StockIssue, StockIssueChange, StockIssueCloseKind, StockItem,
} from "../../api/types";
import { ScanQtyOverlay } from "./ScanQtyOverlay";
import { fmtQty, isWholeUnit, roundQty, validQty } from "./qty";

export type IssueFormMode = "issue" | "return" | "close" | "edit";

export interface IssueFormProps {
  /** „issue” = predare (limita = stocul); „return” / „close” = limita e cât e la destinatar;
   *  „edit” = modificarea predării `issue` (maxim = cantitatea curentă + stocul, minim = ce nu
   *  a fost încă returnat/montat). */
  mode: IssueFormMode;
  /** Destinatar pre-completat (ex. din „La cine e”). */
  initialRecipient?: IssueRecipient | null;
  /** Predarea modificată (obligatoriu pentru mode="edit"). */
  issue?: StockIssue | null;
  onDone: (issue: StockIssue, changes?: StockIssueChange[]) => void;
  onCancel?: () => void;
  /** Server a răspuns `not_editable` la modificare (predarea a fost anulată între timp). */
  onNotEditable?: (id: number) => void;
}

type Line = { stock_item_id: number; qty: number | "" };
type ShortRow = { stock_item_id: number; name: string; unit: string; available: number };
/** La edit `requested` = cu cât se scade (|delta|). */
type OverRow = { stock_item_id: number; name: string; requested: number; holding: number };
/** Ce e la destinatar: articol → cantitate (+ nume/u.m. pentru articole care lipsesc din /stock). */
type Held = Map<number, { quantity: number; name: string | null; unit: string | null }>;

const recKey = (r: { type: string; id: number }) => `${r.type}:${r.id}`;

export function RecipientBadge({ type }: { type: IssueRecipient["type"] }) {
  const { t } = useTranslation();
  return (
    <Badge size="xs" variant="light" color={type === "technician" ? "grape" : "blue"}>
      {t(`freeIssues.recipientType.${type}`)}
    </Badge>
  );
}

/** Căutare destinatari pe server (debounce + gardă pe răspunsuri vechi), opțional doar un tip. */
function useRecipientSearch(onlyType?: IssueRecipientType) {
  const [options, setOptions] = useState<IssueRecipient[]>([]);
  const [search, setSearch] = useState("");
  const seqRef = useRef(0);
  useEffect(() => {
    const seq = ++seqRef.current;
    const h = window.setTimeout(() => {
      api.searchIssueRecipients(search)
        .then((r) => {
          if (seq !== seqRef.current) return;
          setOptions(onlyType ? r.recipients.filter((x) => x.type === onlyType) : r.recipients);
        })
        .catch(() => undefined);
    }, 250);
    return () => window.clearTimeout(h);
  }, [search, onlyType]);
  return { options, search, setSearch };
}

function RecipientSelect({
  label, value, onChange, onlyType, required, placeholder, disabled,
}: {
  label: string;
  value: IssueRecipient | null;
  onChange: (r: IssueRecipient | null) => void;
  onlyType?: IssueRecipientType;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const { options, search, setSearch } = useRecipientSearch(onlyType);
  const byKey = useMemo(() => {
    const m = new Map<string, IssueRecipient>();
    if (value) m.set(recKey(value), value);
    for (const r of options) m.set(recKey(r), r);
    return m;
  }, [options, value]);
  const data = useMemo(() => [...byKey.entries()].map(([k, r]) => ({ value: k, label: r.name })), [byKey]);
  return (
    <Select
      label={
        <Group gap={6} component="span" display="inline-flex">
          {label}
          {value && <RecipientBadge type={value.type} />}
        </Group>
      }
      placeholder={placeholder}
      data={data}
      value={value ? recKey(value) : null}
      onChange={(v) => onChange(v ? byKey.get(v) ?? null : null)}
      searchable
      clearable={!disabled}
      disabled={disabled}
      searchValue={search}
      onSearchChange={setSearch}
      filter={({ options: o }) => o}
      nothingFoundMessage={t("freeIssues.nothingFound")}
      renderOption={({ option }) => {
        const r = byKey.get(option.value);
        return (
          <Group gap="xs" wrap="nowrap" justify="space-between" w="100%">
            <div>
              <Text size="sm">{option.label}</Text>
              {r?.subtitle && <Text size="xs" c="dimmed">{r.subtitle}</Text>}
            </div>
            {r && <RecipientBadge type={r.type} />}
          </Group>
        );
      }}
      required={required}
    />
  );
}

export function IssueForm({ mode, initialRecipient, issue, onDone, onCancel, onNotEditable }: IssueFormProps) {
  const { t } = useTranslation();
  const isIssue = mode === "issue";
  /** Predarea modificată (doar la mode="edit"). */
  const editIssue = mode === "edit" ? issue ?? null : null;
  const isEdit = editIssue != null;
  /** Retur / închidere: articolele și limitele vin din ce e la destinatar. */
  const fromHeld = mode === "return" || mode === "close";
  const [items, setItems] = useState<Map<number, StockItem>>(new Map());
  const [recipient, setRecipient] = useState<IssueRecipient | null>(() => {
    if (editIssue) {
      const r = editIssue.recipient;
      return { type: r.type, id: r.id, name: r.name ?? "—" };
    }
    return mode === "close" && initialRecipient?.type !== "technician" ? null : initialRecipient ?? null;
  });
  const [held, setHeld] = useState<Held>(new Map());
  const [heldLoading, setHeldLoading] = useState(false);
  /** Soldul la destinatar s-a încărcat (la edit: altfel nu impunem minimul — îl verifică serverul). */
  const [heldOk, setHeldOk] = useState(false);
  const [lines, setLines] = useState<Line[]>(() =>
    editIssue ? editIssue.lines.map((l) => ({ stock_item_id: l.stock_item_id, qty: l.quantity })) : [],
  );
  const [note, setNote] = useState(editIssue?.note ?? "");
  const [closeKind, setCloseKind] = useState<StockIssueCloseKind>("installed");
  const [target, setTarget] = useState<IssueRecipient | null>(null);
  const [short, setShort] = useState<ShortRow[]>([]);
  const [over, setOver] = useState<OverRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [addSearch, setAddSearch] = useState("");
  const heldSeq = useRef(0);

  useEffect(() => {
    api.listStock()
      .then((r) => setItems(new Map(r.items.map((i) => [i.id, i]))))
      .catch(() => notifications.show({ color: "red", message: t("freeIssues.loadFailed") }));
  }, [t]);

  // Retur/închidere: ce e acum la destinatarul ales.
  async function loadHeld(r: IssueRecipient | null) {
    const seq = ++heldSeq.current;
    setHeldOk(false);
    if (isIssue || !r) {
      setHeld(new Map());
      setHeldLoading(false);
      return;
    }
    setHeldLoading(true);
    try {
      const res = await api.getHoldings({ type: r.type, id: r.id });
      if (seq !== heldSeq.current) return;
      setHeld(new Map(res.rows.map((h) => [h.stock_item_id, { quantity: h.quantity, name: h.name, unit: h.unit }])));
      setHeldOk(true);
    } catch {
      if (seq === heldSeq.current) notifications.show({ color: "red", message: t("freeIssues.loadFailed") });
    } finally {
      if (seq === heldSeq.current) setHeldLoading(false);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void loadHeld(recipient); }, [mode, recipient?.type, recipient?.id]);

  function changeRecipient(r: IssueRecipient | null) {
    setRecipient(r);
    if (fromHeld) {
      // Lista depinde de destinatar — o luăm de la capăt.
      setLines([]);
      setShort([]);
      setOver([]);
    }
  }

  /** Liniile originale ale predării modificate: articol → linie. */
  const orig = useMemo(
    () => new Map((editIssue?.lines ?? []).map((l) => [l.stock_item_id, l])),
    [editIssue],
  );
  const origQty = (id: number) => orig.get(id)?.quantity ?? 0;
  const stockOf = (id: number) => items.get(id)?.quantity ?? 0;

  const nameOf = (id: number) =>
    items.get(id)?.name ?? held.get(id)?.name ?? orig.get(id)?.stock_item_name ?? `#${id}`;
  const unitOf = (id: number) => items.get(id)?.unit ?? held.get(id)?.unit ?? orig.get(id)?.unit ?? "";
  /** Edit: maximul = cantitatea curentă din predare + stocul (articolele inactive nu pot crește). */
  const editMax = (id: number, it: StockItem | undefined = items.get(id)) =>
    roundQty(origQty(id) + (it && it.active ? it.quantity : 0));
  /** Edit: nu poți scădea sub ce a fost deja returnat/montat — minimul = curent − soldul la destinatar. */
  const minOf = (id: number) =>
    isEdit && heldOk ? Math.max(0, roundQty(origQty(id) - (held.get(id)?.quantity ?? 0))) : 0;
  /** Maximul pe articol: stocul la predare, cât e la destinatar la retur/închidere. */
  const limitOf = (id: number) =>
    isIssue ? stockOf(id) : isEdit ? editMax(id) : held.get(id)?.quantity ?? 0;
  const editAvail = (cur: number, stock: number, unit: string) =>
    t("freeIssues.editAvail", { cur: fmtQty(cur), stock: fmtQty(stock), unit });
  const availText = (id: number) =>
    isIssue
      ? t("freeIssues.inStock", { qty: fmtQty(limitOf(id)), unit: unitOf(id) })
      : isEdit
        ? editAvail(origQty(id), stockOf(id), unitOf(id))
        : t("freeIssues.atRecipient", { qty: fmtQty(limitOf(id)), unit: unitOf(id) });
  const overText = (id: number, limit = limitOf(id)) =>
    isIssue
      ? t("freeIssues.insufficient", { qty: fmtQty(limit), unit: unitOf(id) })
      : t("freeIssues.exceedsHolding", { qty: fmtQty(limit), unit: unitOf(id) });
  /** Mesajul pentru o scădere sub minim (inclusiv ștergerea rândului). */
  const belowMinText = (id: number) => t("freeIssues.belowMin", { min: fmtQty(minOf(id)) });

  const inListFor = (id: number) => {
    const l = lines.find((x) => x.stock_item_id === id);
    return l && typeof l.qty === "number" ? l.qty : 0;
  };

  const addOptions = useMemo(() => {
    const taken = (id: number) => lines.some((l) => l.stock_item_id === id);
    if (isIssue) {
      return [...items.values()]
        .filter((i) => i.active && !taken(i.id))
        .map((i) => ({
          value: String(i.id),
          label: `${i.name} — ${t("freeIssues.inStock", { qty: fmtQty(i.quantity), unit: i.unit })}`,
          disabled: i.quantity <= 0,
        }));
    }
    if (isEdit) {
      // Articole active din stoc + cele scoase din predare (pot fi readăugate chiar dacă stocul e 0).
      return [...items.values()]
        .filter((i) => (i.active || orig.has(i.id)) && !taken(i.id))
        .map((i) => {
          const cur = orig.get(i.id)?.quantity ?? 0;
          return {
            value: String(i.id),
            label: `${i.name} — ${editAvail(cur, i.quantity, i.unit)}`,
            disabled: editMax(i.id, i) <= 0,
          };
        });
    }
    return [...held.entries()]
      .filter(([id, h]) => h.quantity > 0 && !taken(id))
      .map(([id, h]) => {
        const it = items.get(id);
        const name = it?.name ?? h.name ?? `#${id}`;
        const unit = it?.unit ?? h.unit ?? "";
        return { value: String(id), label: `${name} — ${t("freeIssues.atRecipient", { qty: fmtQty(h.quantity), unit })}` };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIssue, isEdit, orig, items, held, lines, t]);

  /** Articol necunoscut (încă neîncărcat din /stock) — nu-l validăm/trimitem încă. */
  const unknownItem = (id: number) => !items.has(id) && (isIssue || (isEdit && !orig.has(id)));

  function lineError(l: Line): string | null {
    const id = l.stock_item_id;
    const unit = unitOf(id);
    if (unknownItem(id)) return null;
    if (!validQty(l.qty, unit)) return t("freeIssues.invalidQty");
    if (isEdit) {
      // Peste maxim: stocul nu ajunge pentru creștere (mesajul verbatim, cu stocul disponibil).
      if (l.qty > limitOf(id)) return t("freeIssues.insufficient", { qty: fmtQty(stockOf(id)), unit });
      if (l.qty < minOf(id)) return belowMinText(id);
      return null;
    }
    if (l.qty > limitOf(id)) return overText(id);
    return null;
  }

  const allValid =
    lines.length > 0 &&
    lines.every((l) => {
      const id = l.stock_item_id;
      if (unknownItem(id)) return false;
      return validQty(l.qty, unitOf(id)) && l.qty <= limitOf(id) && l.qty >= minOf(id);
    }) &&
    // Edit: un rând scos din predare înseamnă cantitate 0 — permis doar dacă minimul e 0.
    (!isEdit || [...orig.keys()].every((id) => lines.some((l) => l.stock_item_id === id) || minOf(id) === 0));
  /** Edit: există o diferență față de predare (cantități, rânduri sau notă). */
  const hasDiff =
    !isEdit ||
    note.trim() !== (editIssue?.note ?? "").trim() ||
    lines.length !== orig.size ||
    lines.some((l) => typeof l.qty !== "number" || roundQty(l.qty) !== roundQty(origQty(l.stock_item_id)) || !orig.has(l.stock_item_id));
  const closeValid =
    mode !== "close" ||
    (closeKind === "installed" ||
      (closeKind === "installed_at_customer" && target != null) ||
      (closeKind === "written_off" && note.trim().length > 0));
  const canSubmit = recipient != null && allValid && closeValid && hasDiff && !(isEdit && heldLoading) && !saving;

  function clearErrors() {
    setShort([]);
    setOver([]);
  }

  function addLine(id: number) {
    const limit = limitOf(id);
    clearErrors();
    // Edit: un articol scos și readăugat revine la cantitatea din predare.
    const q = isEdit && orig.has(id)
      ? origQty(id)
      : limit >= 1 || isWholeUnit(unitOf(id)) ? 1 : roundQty(limit);
    setLines((ls) => (ls.some((l) => l.stock_item_id === id) ? ls : [...ls, { stock_item_id: id, qty: q }]));
  }

  function setLineQty(id: number, v: string | number) {
    clearErrors();
    const q: number | "" = v === "" ? "" : Number(v);
    setLines((ls) => ls.map((l) => (l.stock_item_id === id ? { ...l, qty: q } : l)));
  }

  function removeLine(id: number) {
    if (isEdit && minOf(id) > 0) {
      notifications.show({ color: "red", message: `${nameOf(id)}: ${belowMinText(id)}` });
      return;
    }
    clearErrors();
    setLines((ls) => ls.filter((l) => l.stock_item_id !== id));
  }

  function handleScanAdd(fresh: StockItem, qty: number) {
    clearErrors();
    setItems((m) => {
      const next = new Map(m);
      const old = m.get(fresh.id);
      next.set(fresh.id, { ...fresh, recipe_item: old?.recipe_item ?? fresh.recipe_item });
      return next;
    });
    setLines((ls) => {
      const ex = ls.find((l) => l.stock_item_id === fresh.id);
      if (!ex) return [...ls, { stock_item_id: fresh.id, qty }];
      const cur = typeof ex.qty === "number" ? ex.qty : 0;
      return ls.map((l) => (l.stock_item_id === fresh.id ? { ...l, qty: roundQty(cur + qty) } : l));
    });
  }

  async function submit() {
    if (!recipient || !allValid || !closeValid || !hasDiff) return;
    setSaving(true);
    clearErrors();
    try {
      if (editIssue) {
        const r = await api.updateStockIssue(editIssue.id, {
          lines: lines.map((l) => ({ stock_item_id: l.stock_item_id, qty: roundQty(l.qty as number) })),
          note: note.trim() || null,
        });
        onDone(r.issue, r.changes);
        return;
      }
      const r = await api.createStockIssue({
        kind: mode as Exclude<IssueFormMode, "edit">,
        recipient: { type: recipient.type, id: recipient.id },
        lines: lines.map((l) => ({ stock_item_id: l.stock_item_id, qty: roundQty(l.qty as number) })),
        note: note.trim() || null,
        ...(mode === "close"
          ? {
              close_kind: closeKind,
              target_customer_id: closeKind === "installed_at_customer" ? target?.id ?? null : null,
            }
          : {}),
      });
      onDone(r.issue);
    } catch (e) {
      const d = e instanceof ApiError
        ? (e.detail as { error?: string; items?: unknown[] } | null)
        : null;
      if (d?.error === "insufficient_stock" && Array.isArray(d.items)) {
        const rows = d.items as ShortRow[];
        setShort(rows);
        // Actualizează stocul afișat cu ce e acum pe server.
        setItems((m) => {
          const next = new Map(m);
          for (const row of rows) {
            const it = next.get(row.stock_item_id);
            if (it) next.set(row.stock_item_id, { ...it, quantity: row.available });
          }
          return next;
        });
      } else if (d?.error === "not_editable" && editIssue && onNotEditable) {
        notifications.show({ color: "red", message: t("freeIssues.errors.not_editable") });
        onNotEditable(editIssue.id);
      } else if (d?.error === "exceeds_holding" && Array.isArray(d.items)) {
        const rows = d.items as OverRow[];
        setOver(rows);
        // Soldul la destinatar s-a schimbat între timp — afișăm ce e acum.
        setHeld((m) => {
          const next = new Map(m);
          for (const row of rows) {
            const h = next.get(row.stock_item_id);
            next.set(row.stock_item_id, { quantity: row.holding, name: h?.name ?? row.name, unit: h?.unit ?? null });
          }
          return next;
        });
      } else {
        notifications.show({
          color: "red",
          title: t("freeIssues.saveFailed"),
          message: d?.error ? t(`freeIssues.errors.${d.error}`, { defaultValue: d.error }) : String(e),
        });
      }
    } finally {
      setSaving(false);
    }
  }

  const recipeWarn = (it: StockItem | undefined) =>
    (isIssue || isEdit) && it?.recipe_item ? (
      <Group gap={4} wrap="nowrap" align="flex-start">
        <IconAlertTriangle size={14} color="var(--mantine-color-orange-6)" style={{ flexShrink: 0, marginTop: 2 }} />
        <Text size="xs" c="orange.7">{t("freeIssues.recipeWarning")}</Text>
      </Group>
    ) : null;

  const qtyInput = (l: Line) => {
    const unit = unitOf(l.stock_item_id);
    const whole = isWholeUnit(unit);
    return (
      <NumberInput
        value={l.qty}
        onChange={(v) => setLineQty(l.stock_item_id, v)}
        min={0}
        max={limitOf(l.stock_item_id)}
        clampBehavior="none"
        step={1}
        allowNegative={false}
        allowDecimal={!whole}
        decimalScale={whole ? 0 : 4}
        inputMode={whole ? "numeric" : "decimal"}
        error={lineError(l)}
        rightSection={<Text size="xs" c="dimmed" pr={6}>{unit}</Text>}
        w={150}
      />
    );
  };

  const submitLabel = isEdit
    ? t("common.save")
    : isIssue
      ? t("freeIssues.submitIssue")
      : mode === "return"
        ? t("freeIssues.submitReturn")
        : t("freeIssues.submitClose");
  const nothingHeld = fromHeld && recipient != null && !heldLoading && held.size === 0;

  return (
    <Stack>
      <RecipientSelect
        label={mode === "return" ? t("freeIssues.returnFrom") : t("freeIssues.recipient")}
        value={recipient}
        onChange={changeRecipient}
        disabled={isEdit}
        onlyType={mode === "close" ? "technician" : undefined}
        placeholder={mode === "close" ? t("freeIssues.technicianPlaceholder") : t("freeIssues.recipientPlaceholder")}
        required
      />

      {mode === "close" && (
        <>
          <SegmentedControl
            fullWidth
            value={closeKind}
            onChange={(v) => setCloseKind(v as StockIssueCloseKind)}
            data={[
              { value: "installed", label: t("freeIssues.closeKind.installed") },
              { value: "installed_at_customer", label: t("freeIssues.closeKind.installed_at_customer") },
              { value: "written_off", label: t("freeIssues.closeKind.written_off") },
            ]}
          />
          {closeKind === "installed_at_customer" && (
            <RecipientSelect
              label={t("freeIssues.targetCustomer")}
              value={target}
              onChange={setTarget}
              onlyType="customer"
              placeholder={t("freeIssues.customerPlaceholder")}
              required
            />
          )}
        </>
      )}

      {fromHeld && recipient == null ? (
        <Text c="dimmed" size="sm">{t("freeIssues.pickRecipientFirst")}</Text>
      ) : nothingHeld ? (
        <Alert color="gray">{t("freeIssues.nothingHeld")}</Alert>
      ) : (
        <Group align="end" wrap="wrap" gap="xs">
          <Select
            label={t("freeIssues.addItem")}
            placeholder={fromHeld ? t("freeIssues.addHeldPlaceholder") : t("freeIssues.addItemPlaceholder")}
            data={addOptions}
            value={null}
            onChange={(v) => { if (v) addLine(Number(v)); setAddSearch(""); }}
            searchValue={addSearch}
            onSearchChange={setAddSearch}
            searchable
            disabled={heldLoading}
            style={{ flex: 1, minWidth: 220 }}
            nothingFoundMessage={t("freeIssues.nothingFound")}
          />
          <Button
            variant="light"
            leftSection={<IconQrcode size={16} />}
            onClick={() => setScanOpen(true)}
            disabled={heldLoading}
          >
            {t("freeIssues.scanBtn")}
          </Button>
        </Group>
      )}

      {lines.length === 0 ? (
        isEdit ? (
          <Alert color="orange">{t("freeIssues.editEmpty")}</Alert>
        ) : (
          (isIssue || (recipient != null && !nothingHeld)) && <Text c="dimmed" size="sm">{t("freeIssues.noLines")}</Text>
        )
      ) : (
        <>
          <ResponsiveTable
            mobile={{
              rows: lines,
              rowKey: (l) => l.stock_item_id,
              title: (l) => nameOf(l.stock_item_id),
              subtitle: (l) => availText(l.stock_item_id),
              fields: [
                { label: t("freeIssues.colQty"), render: (l) => qtyInput(l) },
                { label: "", render: (l) => recipeWarn(items.get(l.stock_item_id)), hidden: (l) => !recipeWarn(items.get(l.stock_item_id)) },
              ],
              actions: (l) => (
                <ActionIcon variant="light" color="red" size="lg" onClick={() => removeLine(l.stock_item_id)} aria-label={t("common.delete")}>
                  <IconTrash size={18} />
                </ActionIcon>
              ),
              alwaysOpen: true,
            }}
          >
            <Table withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("freeIssues.colItem")}</Table.Th>
                  <Table.Th>{t("freeIssues.colQty")}</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {lines.map((l) => {
                  const it = items.get(l.stock_item_id);
                  return (
                    <Table.Tr key={l.stock_item_id}>
                      <Table.Td>
                        <Text size="sm" fw={500}>{nameOf(l.stock_item_id)}</Text>
                        <Text size="xs" c="dimmed">{availText(l.stock_item_id)}</Text>
                        {recipeWarn(it)}
                      </Table.Td>
                      <Table.Td>{qtyInput(l)}</Table.Td>
                      <Table.Td>
                        <ActionIcon variant="subtle" color="red" onClick={() => removeLine(l.stock_item_id)} aria-label={t("common.delete")}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ResponsiveTable>
        </>
      )}

      {short.length > 0 && (
        <Alert color="red" title={t("freeIssues.stockChanged")}>
          <Stack gap={2}>
            {short.map((s) => (
              <Text key={s.stock_item_id} size="sm">
                {s.name}: {t("freeIssues.insufficient", { qty: fmtQty(s.available), unit: s.unit })}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      {over.length > 0 && (
        <Alert color="red" title={t("freeIssues.holdingChanged")}>
          <Stack gap={2}>
            {over.map((s) => (
              <Text key={s.stock_item_id} size="sm">
                {s.name}:{" "}
                {isEdit
                  ? t("freeIssues.editExceedsHolding", {
                      qty: fmtQty(s.requested), holding: fmtQty(s.holding), unit: unitOf(s.stock_item_id),
                    })
                  : overText(s.stock_item_id, s.holding)}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      <Textarea
        label={mode === "close" && closeKind === "written_off" ? t("freeIssues.writeOffReason") : t("freeIssues.note")}
        required={mode === "close" && closeKind === "written_off"}
        value={note}
        onChange={(e) => setNote(e.currentTarget.value)}
        autosize
        minRows={2}
      />

      <Group justify="flex-end">
        {onCancel && <Button variant="default" onClick={onCancel}>{t("common.cancel")}</Button>}
        <Button color={isIssue ? "green" : mode === "return" ? "teal" : "blue"} onClick={() => void submit()} disabled={!canSubmit} loading={saving}>
          {submitLabel}
        </Button>
      </Group>

      <ScanQtyOverlay
        opened={scanOpen}
        onClose={() => setScanOpen(false)}
        rows={lines.map((l) => ({
          stock_item_id: l.stock_item_id, name: nameOf(l.stock_item_id), unit: unitOf(l.stock_item_id), qty: l.qty,
        }))}
        limitFor={(item) =>
          isIssue ? item.quantity : isEdit ? editMax(item.id, item) : held.get(item.id)?.quantity ?? 0}
        inListFor={inListFor}
        onAdd={handleScanAdd}
        limitLabel={
          isIssue
            ? undefined
            : isEdit
              ? (item) => editAvail(origQty(item.id), item.quantity, item.unit)
              : (item, limit) => t("freeIssues.atRecipient", { qty: fmtQty(limit), unit: item.unit })
        }
        limitError={
          isIssue
            ? undefined
            : isEdit
              ? (item) => t("freeIssues.insufficient", { qty: fmtQty(item.active ? item.quantity : 0), unit: item.unit })
              : (item, limit) => t("freeIssues.exceedsHolding", { qty: fmtQty(limit), unit: item.unit })
        }
      />
    </Stack>
  );
}
